import React, { useEffect, useState, useCallback } from 'react';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import Tag from '../components/ui/Tag';
import Select from '../components/ui/Select';
import Checkbox from '../components/ui/Checkbox';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import LogoSpinner from '../components/ui/LogoSpinner';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import { useFirmwareApi, formatBytes, shortSha, formatUploaded, firmwareShortHash, firmwareDeleteMessage, targetName } from './firmwareApi';
import UploadFirmwareModal from './UploadFirmwareModal';
import FirmwareIdentifierModal from './FirmwareIdentifierModal';
import { CATEGORY_ORDER, CATEGORY_LABELS, firmwarePath } from './types';
import type { Firmware, FirmwareFile, FirmwareCategory, FirmwareListResponse } from './types';

interface Props {
  canManage: boolean;
  canDelete: boolean;
  manageMode: boolean;
}

type ModalState =
  | { mode: 'upload'; category: FirmwareCategory; existing?: null }
  | { mode: 'attach'; category: FirmwareCategory; existing: Firmware }
  | { mode: 'addTarget'; category: FirmwareCategory; existing: Firmware }
  | { mode: 'edit'; category: FirmwareCategory; existing: Firmware }
  | null;

// ── small presentational bits ──────────────────────────────────────────────

const ModelTags: React.FC<{ keys: string[] | null; nameOf: (k: string) => string }> = ({ keys, nameOf }) =>
  keys && keys.length ? (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {keys.map((k) => (
        <Tag key={k} tone="info" size="sm">{nameOf(k)}</Tag>
      ))}
    </div>
  ) : null;

const ShaChip: React.FC<{ sha: string | null }> = ({ sha }) => {
  const toast = useToast();
  if (!sha) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(sha); toast.success('SHA-256 copied'); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-on-surface-variant">SHA-256</span>
      <Tag variant="code" mono size="sm" title={sha}>{shortSha(sha)}</Tag>
      <button onClick={copy} className={buttonClass('quiet', 'sm')}>copy</button>
    </span>
  );
};

const ChatIcon: React.FC = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

// A comment-count pill that links to the build's page (the thread lives there
// now — the Download card no longer expands comments inline).
const CommentLink: React.FC<{ fw: Firmware }> = ({ fw }) => {
  const count = fw.comment_count || 0;
  return (
    <Link
      to={firmwarePath(fw.category, firmwareShortHash(fw))}
      className="inline-flex items-center gap-1.5 text-xs text-on-surface-variant hover:text-primary transition-colors"
    >
      <ChatIcon />
      <span>{count > 0 ? `${count} comment${count === 1 ? '' : 's'}` : 'Comment'}</span>
    </Link>
  );
};

// Short-hash chip that links to the build's shareable page + copies the link.
const HashLink: React.FC<{ fw: Firmware }> = ({ fw }) => {
  const toast = useToast();
  const sh = firmwareShortHash(fw);
  if (!sh) return null;
  const page = firmwarePath(fw.category, sh);
  const copyLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    try { await navigator.clipboard.writeText(`${window.location.origin}${page}`); toast.success('Link copied'); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <Tooltip label="Open shareable page"><Link to={page} className="text-primary/90 hover:text-primary font-mono">#{sh}</Link></Tooltip>
      <button onClick={copyLink} className={buttonClass('quiet', 'sm')}>link</button>
    </span>
  );
};

// ── target-file row ────────────────────────────────────────────────────────
// A build has one row per build target. The Download button lives HERE, not on
// the card header, because "which file do I flash" is a per-target question.

const SigChip: React.FC<{ file: FirmwareFile; busy: boolean; onDownloadSig: (f: FirmwareFile) => void }> = ({ file, busy, onDownloadSig }) =>
  file.has_signature ? (
    <Tooltip label={`Download ${file.sig_file_name} (${formatBytes(file.sig_file_size_bytes)})`}>
      <button
        onClick={() => onDownloadSig(file)}
        disabled={busy}
        className={buttonClass('quiet', 'sm')}
      >
        ↓ signed
      </button>
    </Tooltip>
  ) : (
    <Tag size="sm" title="No detached signature published for this file">unsigned</Tag>
  );

interface TargetRowProps {
  file: FirmwareFile;
  active: boolean;
  nameOf: (k: string) => string;
  busy: boolean;
  onDownload: (f: FirmwareFile) => void;
  onDownloadSig: (f: FirmwareFile) => void;
}

const TargetRow: React.FC<TargetRowProps> = ({ file, active, nameOf, busy, onDownload, onDownloadSig }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-t border-gray-800/50 first:border-t-0">
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-on-surface font-mono break-all">{targetName(file)}</span>
        <SigChip file={file} busy={busy} onDownloadSig={onDownloadSig} />
      </div>
      <div className="text-[11px] text-on-surface-variant mt-0.5 font-mono break-all">
        {[formatBytes(file.file_size_bytes), file.file_name].filter(Boolean).join(' · ')}
      </div>
      <ModelTags keys={file.applicable_models} nameOf={nameOf} />
      <div className="text-[11px] text-on-surface-variant mt-1.5">
        <ShaChip sha={file.sha256} />
      </div>
    </div>
    {active ? (
      <button onClick={() => onDownload(file)} disabled={busy} className={`${buttonClass('filled', 'md')} flex-none`}>
        ↓ Download
      </button>
    ) : (
      <Tooltip label="Download"><IconButton label="Download" onClick={() => onDownload(file)} disabled={busy}>
        ↓
      </IconButton></Tooltip>
    )}
  </div>
);

// ── firmware card ──────────────────────────────────────────────────────────

interface CardProps {
  fw: Firmware;
  files: FirmwareFile[]; // already model-filtered by the caller
  manageMode: boolean;
  canDelete: boolean;
  nameOf: (k: string) => string;
  busy: boolean;
  onDownloadFile: (f: FirmwareFile) => void;
  onDownloadSig: (f: FirmwareFile) => void;
  onEdit: (fw: Firmware) => void;
  onAddTarget: (fw: Firmware) => void;
  onRetire: (fw: Firmware) => void;
  onActivate: (fw: Firmware) => void;
  onReactivate: (fw: Firmware) => void;
  onDelete: (fw: Firmware) => void;
}

const FirmwareCard: React.FC<CardProps> = ({ fw, files, manageMode, canDelete, nameOf, busy, onDownloadFile, onDownloadSig, onEdit, onAddTarget, onRetire, onActivate, onReactivate, onDelete }) => {
  const active = fw.is_active && !fw.is_archived;
  const meta = [fw.version, fw.branch].filter(Boolean).join(' · ');
  const hiddenCount = fw.files.length - files.length;

  return (
    <Panel label={fw.label} padding="sm" className={`mb-2.5 ${active ? 'bg-gradient-to-br from-primary/10 to-yellow-600/5' : ''}`}>
      {/* Header: the BUILD (one commit) */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {fw.is_archived ? (
            <Tag>Retired</Tag>
          ) : fw.is_active ? (
            <Tag tone="good">Active</Tag>
          ) : (
            <Tag>Inactive</Tag>
          )}
          <Tooltip label="Open firmware page"><Link to={firmwarePath(fw.category, firmwareShortHash(fw))} className="text-white font-semibold truncate hover:text-primary transition-colors">{fw.label}</Link></Tooltip>
          {fw.files.length > 1 && (
            <Tag size="sm">{fw.files.length} targets</Tag>
          )}
        </div>
        {meta && <div className="text-xs text-on-surface-variant mt-1 font-mono break-all">{meta}</div>}
        <div className="text-[11px] text-on-surface-variant mt-2 flex items-center gap-2 flex-wrap">
          <HashLink fw={fw} />
          {fw.uploaded_at && (
            <span className="text-on-surface-variant">· uploaded {formatUploaded(fw.uploaded_at)}{fw.uploaded_by_name ? ` by ${fw.uploaded_by_name}` : ''}</span>
          )}
        </div>
        {fw.notes && <p className="text-xs text-on-surface-variant mt-2 whitespace-pre-wrap">{fw.notes}</p>}
      </div>

      {/* Target files — one row per build target, each with its own download */}
      <div className="mt-3">
        {files.map((f) => (
          <TargetRow key={f.id} file={f} active={active} nameOf={nameOf} busy={busy} onDownload={onDownloadFile} onDownloadSig={onDownloadSig} />
        ))}
        {hiddenCount > 0 && (
          <div className="text-[11px] text-on-surface-variant pt-2 border-t border-gray-800/50">
            {hiddenCount} more {hiddenCount === 1 ? 'target is' : 'targets are'} not tagged for the selected model.
          </div>
        )}
      </div>

      {/* Footer: link to the build's page (thread + per-target management) + manager controls */}
      <div className="mt-3 pt-2.5 border-t border-gray-800/70">
        <CommentLink fw={fw} />
        <Reveal show={manageMode}>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <button onClick={() => onEdit(fw)} disabled={busy} className={buttonClass('quiet', 'sm')}>Edit</button>
            <button onClick={() => onAddTarget(fw)} disabled={busy} className={buttonClass('quiet', 'sm')}>+ Add target file</button>
            {fw.is_archived ? (
              <button onClick={() => onReactivate(fw)} disabled={busy} className={buttonClass('quiet', 'sm')}>Re-activate</button>
            ) : (
              <>
                {!fw.is_active && (
                  <button onClick={() => onActivate(fw)} disabled={busy} className={buttonClass('quiet', 'sm')}>Activate</button>
                )}
                <button onClick={() => onRetire(fw)} disabled={busy} className={buttonClass('quiet', 'sm')}>Retire</button>
              </>
            )}
            {canDelete && (
              <button onClick={() => onDelete(fw)} disabled={busy} className={`${buttonClass('danger-quiet', 'sm')} ml-auto`}>Delete</button>
            )}
          </div>
        </Reveal>
      </div>
    </Panel>
  );
};

// ── main ───────────────────────────────────────────────────────────────────

const DownloadTab: React.FC<Props> = ({ canManage, canDelete, manageMode }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const fw = useFirmwareApi();
  const [data, setData] = useState<FirmwareListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>('all');
  const [modal, setModal] = useState<ModalState>(null);
  const [openOlder, setOpenOlder] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [identifying, setIdentifying] = useState(false);
  const [showTelemetry, setShowTelemetry] = useState(false); // file-less AP labels hidden by default

  const load = useCallback(async () => {
    try {
      const res = await fw.list();
      setData(res);
      setDenied(false);
    } catch (e: any) {
      if (e?.status === 403) setDenied(true);
      else toast.error('Failed to load firmware');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const models = data?.models || [];
  const archPresets = data?.archPresets || [];
  const nameOf = useCallback((k: string) => models.find((m) => m.key === k)?.name || k, [models]);

  // Build-level: `applicable_models` is the UNION across the build's target files,
  // so a card survives the filter if ANY of its targets is tagged for the model.
  const matchesModel = (f: Firmware) => selectedModel === 'all' || (f.applicable_models || []).includes(selectedModel);
  // File-level: within a surviving card, show only the targets that match — this
  // is what makes picking a model show you the one file you should flash.
  const filesFor = (f: Firmware) =>
    selectedModel === 'all'
      ? f.files
      : f.files.filter((x) => (x.applicable_models || []).includes(selectedModel));

  const mutate = async (fwItem: Firmware, patch: Parameters<typeof fw.update>[1], okMsg: string) => {
    setBusyId(fwItem.id);
    try { await fw.update(fwItem.id, patch); toast.success(okMsg); await load(); }
    catch (e: any) { toast.error(e?.message || 'Update failed'); }
    finally { setBusyId(null); }
  };

  const onDownloadFile = async (file: FirmwareFile) => {
    setBusyId(file.firmware_id);
    try { await fw.downloadFile(file); }
    catch (e: any) { toast.error(e?.message || 'Download failed'); }
    finally { setBusyId(null); }
  };

  const onDownloadSig = async (file: FirmwareFile) => {
    setBusyId(file.firmware_id);
    try { await fw.downloadSignature(file); }
    catch (e: any) { toast.error(e?.message || 'Signature download failed'); }
    finally { setBusyId(null); }
  };

  const onDelete = async (f: Firmware) => {
    const ok = await confirm({
      title: 'Delete firmware permanently',
      tone: 'danger',
      confirmLabel: 'Delete permanently',
      message: firmwareDeleteMessage(f),
    });
    if (!ok) return;
    setBusyId(f.id);
    try { await fw.remove(f.id); toast.success('Firmware deleted'); await load(); }
    catch (e: any) { toast.error(e?.message || 'Delete failed'); }
    finally { setBusyId(null); }
  };

  if (loading) return <LogoSpinner label="Loading firmware" />;
  if (denied) {
    return <EmptyState variant="area">You don't have access to firmware downloads.</EmptyState>;
  }

  return (
    <FadeIn>
      {/* Firmware Identifier — drop a file / type a hash to identify a build */}
      <div className="mb-4">
        <button
          onClick={() => setIdentifying(true)}
          className={buttonClass('tonal', 'md')}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            <circle cx="11" cy="11" r="7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M21 21l-4.35-4.35" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Firmware Identifier
        </button>
      </div>

      {/* Model filter */}
      <Panel label="Model filter" padding="sm" className="mb-4">
       <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs text-on-surface-variant">Downloading for:</span>
        <Select
          label="Downloading for"
          size="sm"
          width="fit"
          value={selectedModel}
          onChange={setSelectedModel}
          options={[{ key: 'all', label: 'All models' }, ...models.map((m) => ({ key: m.key, label: m.name }))]}
        />
        <Appear show={selectedModel !== 'all'} className="text-xs text-on-surface-variant">showing builds tagged for this model</Appear>
       </div>
      </Panel>

      {/* Telemetry-only labels are hidden by default in Manage mode — they're
          file-less AP builds, only useful when attaching a firmware to one. */}
      <Reveal show={manageMode && (data?.attachable?.length ?? 0) > 0}>
        <Checkbox
          checked={showTelemetry}
          onChange={(e) => setShowTelemetry(e.target.checked)}
          className="mb-4 -mt-1 text-xs text-on-surface-variant"
          label={
            <>
              <span>Show telemetry-only labels ({data?.attachable?.length})</span>{' '}
              <span className="text-[11px] text-on-surface-variant font-normal">— file-less AP builds you can attach firmware to</span>
            </>
          }
        />
      </Reveal>

      {CATEGORY_ORDER.filter((cat) => data?.categories?.[cat]).map((cat) => {
        const all = (data?.categories?.[cat] || []).filter(matchesModel);
        const active = all.filter((f) => f.is_active && !f.is_archived);
        const older = all.filter((f) => !(f.is_active && !f.is_archived));
        // attachable is px4-only by construction (the service filters category='px4'),
        // so this px4 gate stays in sync with the toggle's visibility gate below.
        // If another category ever becomes attachable, both spots need updating.
        const attachRows = cat === 'px4' && manageMode && showTelemetry ? (data?.attachable || []) : [];
        const olderOpen = openOlder[cat] ?? manageMode;

        return (
          <section key={cat} className="mb-6">
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">{CATEGORY_LABELS[cat]}</h3>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-on-surface-variant">{active.length ? `${active.length} active` : 'no active'}</span>
                {manageMode && (
                  <button onClick={() => setModal({ mode: 'upload', category: cat })} className={buttonClass('quiet', 'sm')}>
                    + Upload
                  </button>
                )}
              </div>
            </div>

            {active.length === 0 && attachRows.length === 0 && (
              <EmptyState variant="area">
                No active {CATEGORY_LABELS[cat]}{selectedModel !== 'all' ? ` tagged for ${nameOf(selectedModel)}` : ' published'}.
              </EmptyState>
            )}

            {active.map((f) => (
              <FirmwareCard key={f.id} fw={f} files={filesFor(f)} manageMode={manageMode} canDelete={canDelete} nameOf={nameOf} busy={busyId === f.id}
                onDownloadFile={onDownloadFile}
                onDownloadSig={onDownloadSig}
                onEdit={(x) => setModal({ mode: 'edit', category: cat, existing: x })}
                onAddTarget={(x) => setModal({ mode: 'addTarget', category: cat, existing: x })}
                onRetire={(x) => mutate(x, { is_archived: true }, 'Retired')}
                onActivate={(x) => mutate(x, { is_active: true }, 'Activated')}
                onReactivate={(x) => mutate(x, { is_archived: false, is_active: true }, 'Re-activated')}
                onDelete={onDelete}
              />
            ))}

            {/* Attach rows — file-less PX4 telemetry labels (manage mode) */}
            {attachRows.map((row) => (
              <Panel key={`att-${row.id}`} label={`Telemetry only: ${row.label}`} padding="sm" className="mb-2.5">
               <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-on-surface-variant min-w-0">
                  <Tag className="mr-2">telemetry only</Tag>
                  <span className="text-white font-mono">{row.git_hash?.slice(0, 9)}</span>
                  <span className="text-on-surface-variant"> · labeled "{row.label}" · no file</span>
                </span>
                <button onClick={() => setModal({ mode: 'attach', category: 'px4', existing: row })} className={`${buttonClass('quiet', 'sm')} flex-none`}>
                  Attach firmware
                </button>
               </div>
              </Panel>
            ))}

            {older.length > 0 && (
              <div className="mt-1">
                <button onClick={() => setOpenOlder((p) => ({ ...p, [cat]: !olderOpen }))} className="text-xs text-on-surface-variant hover:text-on-surface py-1.5">
                  {olderOpen ? '▾' : '▸'} Older builds ({older.length})
                </button>
                <Reveal show={!!olderOpen}>
                  <div className="pl-1">
                    {older.map((f) => (
                      <FirmwareCard key={f.id} fw={f} files={filesFor(f)} manageMode={manageMode} canDelete={canDelete} nameOf={nameOf} busy={busyId === f.id}
                        onDownloadFile={onDownloadFile}
                        onDownloadSig={onDownloadSig}
                        onAddTarget={(x) => setModal({ mode: 'addTarget', category: cat, existing: x })}
                        onEdit={(x) => setModal({ mode: 'edit', category: cat, existing: x })}
                        onRetire={(x) => mutate(x, { is_archived: true }, 'Retired')}
                        onActivate={(x) => mutate(x, { is_active: true }, 'Activated')}
                        onReactivate={(x) => mutate(x, { is_archived: false, is_active: true }, 'Re-activated')}
                        onDelete={onDelete}
                      />
                    ))}
                  </div>
                </Reveal>
              </div>
            )}
          </section>
        );
      })}

      {modal && (
        <UploadFirmwareModal
          mode={modal.mode}
          category={modal.category}
          models={models}
          archPresets={archPresets}
          existing={modal.existing}
          attachable={data?.attachable || []}
          /* Published builds in this category: lets the modal recognize that a
             dropped file's hash belongs to an existing build and switch to
             add-a-target instead of trying (and failing) to publish a new one. */
          published={data?.categories?.[modal.category] || []}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}

      {identifying && (
        <FirmwareIdentifierModal archPresets={archPresets} onClose={() => setIdentifying(false)} />
      )}
    </FadeIn>
  );
};

export default DownloadTab;
